import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import { SITE_URL } from "@/lib/seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { pillarArticles } from "@/mocks/pillarArticles";

const getArticleIndex = (slug: string): number => {
  const idx = pillarArticles.findIndex((a) => a.slug === slug);
  return idx >= 0 ? idx : -1;
};

export default function GuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const idx = useMemo(() => getArticleIndex(slug || ""), [slug]);
  const article = idx >= 0 ? pillarArticles[idx] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="pt-28 md:pt-40 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">{t("notFound.title", "Guide Not Found")}</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:underline cursor-pointer">
            {t("notFound.back", "Back to Home")}
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const k = `pillar.${idx}`;
  const published = "June 15, 2026";
  const updated = "August 20, 2026";

  const faqKeys = [
    { q: `${k}.faq_q1`, a: `${k}.faq_a1` },
    { q: `${k}.faq_q2`, a: `${k}.faq_a2` },
    { q: `${k}.faq_q3`, a: `${k}.faq_a3` },
  ];

  const bodySections = [
    { h2: `${k}.h2_1`, p: [`${k}.p1`, `${k}.p2`] },
    { h2: `${k}.h2_2`, p: [`${k}.p3`, `${k}.p4`] },
    { h2: `${k}.h2_3`, p: [`${k}.p5`] },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: t(`${k}.title`),
          description: t(`${k}.subtitle`),
          image: article.image,
          datePublished: "2026-06-15",
          dateModified: "2026-08-20",
          author: { "@type": "Organization", name: "USFansSheet" },
          publisher: {
            "@type": "Organization",
            name: "USFansSheet",
            logo: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2277e9ffbdee9fd651886f1aeeba0d56.jpeg",
          },
          mainEntityOfPage: `${SITE_URL}/guide/${article.slug}`,
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: t("breadcrumbHome"),
              item: `${SITE_URL}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: t(`${k}.title`),
              item: `${SITE_URL}/guide/${article.slug}`,
            },
          ],
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqKeys
            .map((item) => {
              const qText = t(item.q);
              const aText = t(item.a);
              if (!qText || qText === item.q) return null;
              return {
                "@type": "Question",
                name: qText,
                acceptedAnswer: { "@type": "Answer", text: aText },
              };
            })
            .filter(Boolean),
        }}
      />
      <SiteHeader />
      <main className="pt-24 md:pt-32 pb-14 md:pb-20">
        <article className="mx-auto max-w-3xl px-4 md:px-6">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              {t("breadcrumbHome")}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/#guides" className="hover:text-primary-600 transition-colors cursor-pointer">
              {t("nav.guides")}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400 truncate max-w-[200px]">{t(`${k}.title`)}</span>
          </nav>

          {/* Header */}
          <header className="mt-6">
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-book-open-line text-sm"></i>
              {t(article.tagKey)}
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold leading-tight text-foreground-950">
              {t(`${k}.title`)}
            </h1>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-foreground-600">
              {t(`${k}.subtitle`)}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {t("published")}: {published}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line"></i>
                {t("updated")}: {updated}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readingTime} {t("readingTime")}
              </span>
            </div>
          </header>

          {/* Cover image */}
          <div className="mt-8 h-56 md:h-96 w-full overflow-hidden rounded-2xl">
            <img
              src={article.image}
              alt={t(`${k}.title`)}
              title={t(`${k}.title`)}
              className="h-full w-full object-cover object-top"
            />
          </div>

          {/* Intro */}
          <div className="mt-8 space-y-4 text-base md:text-lg leading-relaxed text-foreground-700">
            <p>{t(`${k}.intro1`)}</p>
            <p>{t(`${k}.intro2`)}</p>
          </div>

          {/* Body sections */}
          {bodySections.map((section, si) => {
            const h2Text = t(section.h2);
            if (!h2Text || h2Text === section.h2) return null;
            return (
              <section className="mt-10" key={si}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{h2Text}</h2>
                {section.p.map((pk, pi) => {
                  const text = t(pk);
                  if (!text || text === pk) return null;
                  return (
                    <p key={pi} className="mt-4 text-base leading-relaxed text-foreground-700">
                      {text}
                    </p>
                  );
                })}
              </section>
            );
          })}

          {/* Tips list (if available) */}
          {t(`${k}.tips_intro`) !== `${k}.tips_intro` && (
            <section className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t(`${k}.tips_h2`)}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t(`${k}.tips_intro`)}</p>
              <ul className="mt-4 space-y-3">
                {[1, 2, 3, 4].map((n) => {
                  const tip = t(`${k}.tip_${n}`);
                  if (!tip || tip === `${k}.tip_${n}`) return null;
                  return (
                    <li key={n} className="flex items-start gap-3 text-base text-foreground-700">
                      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                        <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                      <span>{tip}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t(`${k}.faq_title`)}</h2>
            <div className="mt-6 space-y-3">
              {faqKeys.map((item, i) => {
                const qText = t(item.q);
                const aText = t(item.a);
                if (!qText || qText === item.q) return null;
                return (
                  <div key={item.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-900">{qText}</span>
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                        <i className={`text-base transition-transform ${openFaq === i ? "rotate-45" : ""} ri-add-line`}></i>
                      </span>
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-5">
                        <p className="text-sm leading-relaxed text-foreground-600">{aText}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Disclaimer */}
          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
            <p className="text-xs md:text-sm leading-relaxed text-foreground-500">{t(`${k}.disclaimer`)}</p>
          </div>

          {/* CTA */}
          <div className="mt-8 rounded-2xl bg-primary-950 p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">{t(`${k}.cta_title`)}</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-background-100/80">{t(`${k}.cta_text`)}</p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-800 hover:bg-background-100 transition-colors cursor-pointer"
              >
                {t(`${k}.cta_btn`)}
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <Link
                to="/"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-100/40 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-background-100/10 transition-colors cursor-pointer"
              >
                {t("backHome")}
              </Link>
            </div>
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}